import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { z } from "zod";

import PoDetails from "./PODetails";
import ProjectAddress from "./ProjectAddress";
import ProjectBasicInfo from "./ProjectBasicInfo";
import ProjectRes from "./ProjectRes";
import ProjectsDetails from "./ProjectsDetails";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import * as Dialog from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import useQueryParams from "@/hooks/useQueryParams";
import { ILeads } from "@/interfaces/project-register.interface";
import { IProject } from "@/interfaces/project.interface";
import { renderFullName } from "@/lib/utils";
import { claimsApi } from "@/redux/api/claimsQuery";
import { docketApi } from "@/redux/api/docketQuery";
import {
  useGetSingleLeadQuery,
  useUpdateLeadsStatusMutation,
} from "@/redux/api/prQuery";
import {
  projectApi,
  useAlterProjectMutation,
  useGetSingleProjectQuery,
} from "@/redux/api/projectsQuery";
import {
  LeadsWonSchema,
  TAddProjectSchema,
  UpdateProjectSchema,
} from "@/schemas/project.schema";
import { customToast } from "@/utils/custom-toast";
import { toUTC } from "@/utils/helper-functions/date-time.helper";
import {
  filterEmptyValues,
  getDirtyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

const useFormattedValues = (
  leadsId: string | undefined,
  leadsSingleData: ILeads | undefined,
  data: IProject | undefined,
): TAddProjectSchema => {
  return useMemo(() => {
    if (leadsId) {
      return {
        projectName: leadsSingleData?.projectName || "",
        startDate: "",
        address: {
          latitude: leadsSingleData?.address?.latitude || "",
          longitude: leadsSingleData?.address?.longitude || "",
          label: leadsSingleData?.address?.label || "",
          radius: +(leadsSingleData?.address?.radius ?? 200),
          areaLocation: leadsSingleData?.address?.areaLocation || "",
        },
        customerProjectReference: leadsSingleData?.projectNumber || "",
        ourReferenceNumber: leadsSingleData?.referenceNumber || "",
        budgetHours: 0,
        customerId: leadsSingleData?.customerId || "",
        customerDefaultName: leadsSingleData?.customer?.name || "",
        ourProjectTeam:
          leadsSingleData?.ourTeam
            ?.map((item) => item?.id)
            .filter((id): id is string => id !== undefined) || [],
        defaultOurProjectTeam: leadsSingleData?.ourTeam?.map((item) => ({
          label: renderFullName({
            params: item,
          }),
          value: item?.id || "",
        })),
        customerProjectTeam:
          leadsSingleData?.customerUsers
            ?.map((item) => item?.id)
            .filter((id): id is string => id !== undefined) || [],
        defaultCustomerProjectTeam: leadsSingleData?.customerUsers?.map(
          (item) => ({
            label: renderFullName({
              params: item,
            }),
            value: item?.id || "",
          }),
        ),
        inductions: [],
      };
    } else {
      return {
        projectName: data?.name || "",
        startDate: data?.startDate || "",
        address: {
          latitude: data?.address?.latitude || "",
          longitude: data?.address?.longitude || "",
          label: data?.address?.label || "",
          radius: data?.address?.radius || 0,
          areaLocation: data?.address?.areaLocation || "",
        },
        customerProjectReference: data?.customerProjectReference || "",
        ourReferenceNumber: data?.ourReferenceNumber || "",
        budgetHours: data?.budgetHours || 0,
        customerId: data?.customerId || "",
        customerDefaultName: data?.customer?.name || "",
        ourProjectTeam: data?.ourProjectTeam?.map((item) => item?.id) || [],
        defaultOurProjectTeam:
          data?.ourProjectTeam?.map((item) => ({
            label: renderFullName({
              params: item,
            }),
            value: item?.id || "",
          })) || [],
        customerProjectTeam:
          data?.customerProjectTeam?.map((item) => item?.id) || [],
        defaultCustomerProjectTeam:
          data?.customerProjectTeam?.map((item) => ({
            label: renderFullName({
              params: item,
            }),
            value: item?.id || "",
          })) || [],
        inductions:
          data?.projectInductions?.map((item) => ({
            id: item?.id,
            name: item?.name,
            value: item?.value,
          })) || [],
      };
    }
  }, [leadsId, leadsSingleData, data]);
};

export const UpdateProject = ({
  isUpdateModalOpen,
  projectId,
  toggleModal,
  // refetch,
}: {
  projectId?: string;
  isUpdateModalOpen: boolean;
  toggleModal: () => void;
  // refetch: () => void;
}): React.JSX.Element => {
  const dispatch = useDispatch();

  const { keys, clearParam } = useQueryParams();
  const leadsId = keys.leadsId as string;

  const { data: singleData, ...rest } = useGetSingleProjectQuery(
    {
      projectId: projectId || "",
    },
    {
      skip: !projectId,
    },
  );

  const { data: leadsData, ...rest2 } = useGetSingleLeadQuery(
    {
      id: leadsId,
    },
    {
      skip: !leadsId,
    },
  );
  const [alterProject, { isLoading: isUpdating }] = useAlterProjectMutation();
  const [updateLeadsStatus, { isLoading: isMarksAsWonMutating }] =
    useUpdateLeadsStatusMutation();

  const data = singleData as IProject;
  const leadsSingleData = leadsData;

  const CurrentSchema = leadsId ? LeadsWonSchema : UpdateProjectSchema;

  type TCurrentSchema = z.infer<typeof CurrentSchema>;

  const formattedValues = useFormattedValues(leadsId, leadsSingleData, data);
  const form = useForm<TCurrentSchema>({
    mode: "onChange",
    resolver: zodResolver(CurrentSchema),
    values: formattedValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const handleClose = (): void => {
    clearParam("leadsId");
    reset();
    toggleModal();
    dispatch(projectApi.util.invalidateTags(["projects"]));
  };

  const onSubmit = async (data: TCurrentSchema): Promise<void> => {
    let formattedData;

    if (leadsId) {
      formattedData = filterEmptyValues(data);
    } else {
      //@ts-expect-error - due to being some values optional
      formattedData = getDirtyValues(form.formState.dirtyFields, data);
    }
    if (data?.startDate) {
      formattedData = {
        ...formattedData,
        startDate: toUTC(data.startDate),
      };
    } else {
      delete formattedData.startDate;
    }

    const dataToSend = leadsId
      ? {
          ...formattedData,
          status: "WON",
        }
      : formattedData;

    const response = leadsId
      ? await updateLeadsStatus({
          id: leadsId,
          data: parseToFormData(dataToSend, ["poFile"]),
        })
      : await alterProject({
          projectId: projectId ?? "",
          data: dataToSend,
          method: REQUEST_METHODS.PUT,
        });
    validateResponse({
      response,
      onSuccess() {
        customToast.success(PROJECT_CONSTANTS.MESSAGES.UPDATED);
        dispatch(docketApi.util.invalidateTags(["docket"]));
        if (leadsId) {
          dispatch(claimsApi.util.invalidateTags(["claims"]));
        }
        handleClose();
      },
    });
  };
  const modalTitle = leadsId ? "Mark as WON" : "Update Project";
  const actionText = leadsId ? "Confirm as WON" : "Save Changes";

  return (
    <FormDialog
      isOpen={isUpdateModalOpen}
      toggleModal={handleClose}
      title={modalTitle}
      isFormDirty={form.formState.isDirty}
      className='sm:max-w-lg md:max-w-5xl md:max-h-[96%] scrollbar-thin '
    >
      <FetchWrapper {...(leadsId ? rest2 : rest)}>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {leadsId && <PoDetails />}
            <div className='grid grid-cols-1 gap-6 mt-4 text-xs lg:grid-cols-3'>
              <ProjectBasicInfo />
              <ProjectAddress />
              <ProjectsDetails isMarkAsWon={!!leadsId} />
              <ProjectRes data={data} action='update' />
            </div>
            <Dialog.DialogFooter className='flex items-center justify-between md:mt-8'>
              <div className='flex items-center justify-between w-full'>
                <Button
                  size={"lg"}
                  type='button'
                  variant={"ghost"}
                  onClick={() => reset()}
                >
                  Discard
                </Button>

                <Button
                  size={"lg"}
                  type='submit'
                  isLoading={isUpdating || isMarksAsWonMutating}
                  disabled={!isDirty && !leadsId}
                  message='Saving...'
                >
                  {actionText}
                </Button>
              </div>
            </Dialog.DialogFooter>
          </form>
        </Form>
      </FetchWrapper>
    </FormDialog>
  );
};
